import React, { useState } from 'react';
import { useSystem } from '@ohif/core';
import { useModal } from '@ohif/ui-next';

import neuroplanIcon from '../../../assets/neuroplan-logo/icon.png';
import neuroplanLogo from '../../../assets/neuroplan-logo/horizontal-dark-logo.png';
import { useAuth } from '../../context/AuthContext';
import './Topbar.css';

interface TopbarProps {
  title: string;
  brandDisplay?: 'wordmark' | 'icon';
  leadingContent?: React.ReactNode;
  className?: string;
}

const LOGO_SOURCES = [neuroplanLogo];

const getInitials = (value: string) =>
  value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('') || 'U';

const SettingsFallback = () => (
  <div className="topbar-settings-fallback">
    <p>Account and viewer preferences are managed from this settings panel.</p>
  </div>
);

const Topbar: React.FC<TopbarProps> = ({
  title,
  brandDisplay = 'wordmark',
  leadingContent,
  className = '',
}) => {
  const { logout, user } = useAuth();
  const { servicesManager } = useSystem();
  const { show } = useModal();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [logoIndex, setLogoIndex] = useState(0);

  const handleLogoError = () => {
    setLogoIndex(currentIndex => {
      const nextIndex = currentIndex + 1;
      return nextIndex < LOGO_SOURCES.length ? nextIndex : currentIndex;
    });
  };

  const handleSignOut = () => {
    setIsProfileOpen(false);
    logout();
  };

  const handleSettings = () => {
    setIsProfileOpen(false);
    const { customizationService } = servicesManager.services as any;
    const UserPreferencesModal = customizationService?.getCustomization?.(
      'ohif.userPreferencesModal'
    );

    show({
      content: UserPreferencesModal ?? SettingsFallback,
      title: UserPreferencesModal?.title ?? 'Settings',
      containerClassName: `neuroplan-settings-modal ${
        UserPreferencesModal?.containerClassName ?? 'flex max-w-4xl flex-col'
      }`,
    });
  };

  return (
    <header className={`topbar ${className}`.trim()}>
      <div className="topbar-leading">
        <div
          className="brand-area"
          aria-label="NeuroPlan"
        >
          <div
            className={`topbar-logo-frame ${brandDisplay === 'icon' ? 'topbar-logo-frame-icon' : ''}`}
          >
            {brandDisplay === 'icon' ? (
              <img
                src={neuroplanIcon}
                alt="NeuroPlan"
                className="topbar-brand-icon"
                draggable={false}
              />
            ) : (
              <img
                src={LOGO_SOURCES[logoIndex]}
                onError={handleLogoError}
                alt="NeuroPlan"
                className="topbar-logo-image"
                draggable={false}
              />
            )}
          </div>
        </div>

        {leadingContent && (
          <>
            <div
              className="topbar-divider"
              aria-hidden="true"
            />
            <div className="topbar-leading-content">{leadingContent}</div>
          </>
        )}
      </div>

      <h1 className="navbar-page-title">{title}</h1>

      <div className="profile-menu">
        <button
          type="button"
          className="profile-trigger"
          onClick={() => setIsProfileOpen(currentValue => !currentValue)}
          aria-expanded={isProfileOpen}
          aria-haspopup="menu"
        >
          <div className="profile-avatar">
            <span className="profile-initials">
              {getInitials(user?.full_name || user?.email || 'User')}
            </span>
          </div>

          <div className="profile-text">
            <p className="profile-name">{user?.full_name || user?.email}</p>
            <p className="profile-role">{user?.role === 'admin' ? 'Administrator' : 'Doctor'}</p>
          </div>

          <svg
            className={`chevron-icon ${isProfileOpen ? 'chevron-icon-open' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {isProfileOpen && (
          <div
            className="profile-dropdown"
            role="menu"
          >
            <button
              type="button"
              className="profile-menu-button settings-button"
              onClick={handleSettings}
              role="menuitem"
            >
              <svg
                className="profile-menu-icon"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.8}
                  d="M12 15.5a3.5 3.5 0 100-7 3.5 3.5 0 000 7zM19.4 15a1.7 1.7 0 00.34 1.88l.06.06-2.86 2.86-.06-.06A1.7 1.7 0 0015 19.4a1.7 1.7 0 00-1 .6 1.7 1.7 0 00-.4 1.1V21H9.55v-.1A1.7 1.7 0 008.5 19.4a1.7 1.7 0 00-1.88.34l-.06.06-2.86-2.86.06-.06A1.7 1.7 0 004.1 15a1.7 1.7 0 00-.6-1 1.7 1.7 0 00-1.1-.4H2.3V9.55h.1A1.7 1.7 0 004.1 8.5a1.7 1.7 0 00-.34-1.88l-.06-.06L6.56 3.7l.06.06A1.7 1.7 0 008.5 4.1a1.7 1.7 0 001-.6 1.7 1.7 0 00.4-1.1v-.1h4.05v.1A1.7 1.7 0 0015 4.1a1.7 1.7 0 001.88-.34l.06-.06 2.86 2.86-.06.06A1.7 1.7 0 0019.4 8.5c.14.4.36.75.65 1 .3.25.68.4 1.08.4h.1v4.05h-.1a1.7 1.7 0 00-1.73 1.05z"
                />
              </svg>
              Settings
            </button>
            <button
              type="button"
              className="profile-menu-button signout-button"
              onClick={handleSignOut}
              role="menuitem"
            >
              <svg
                className="profile-menu-icon signout-icon"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              Sign Out
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

export default Topbar;
